import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTenant } from "@/context/tenant-context";
import { apiFetch } from "@/api/client";
import { azureApi } from "@/api/azure";
import { mcpApi } from "@/api/mcp";
import { queryKeys } from "@/lib/query-keys";
import {
  Lock,
  CheckCircle2,
  Loader2,
  Building2,
} from "lucide-react";

export function TenantVaultSettings() {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();

  const { data: vaultStatus } = useQuery({
    queryKey: ["vault-config", tenantId],
    queryFn: () =>
      apiFetch<{ configured: boolean; vaultUrl: string | null; source: string }>(
        `/vault/config?companyId=${tenantId}`,
      ),
  });

  const provisionMutation = useMutation({
    mutationFn: () => azureApi.setupTenantVault(tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vault-config", tenantId] });
    },
  });

  const { data: mcpCatalogue = [] } = useQuery({
    queryKey: queryKeys.mcpCatalogue.all,
    queryFn: () => mcpApi.listCatalogue(),
  });
  const hasTenantScopedMcps = mcpCatalogue.some((c) => c.secretScope === "tenant");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Lock size={18} />
          Key Vault
        </CardTitle>
      </CardHeader>
      <CardContent>
        {vaultStatus?.configured && vaultStatus.vaultUrl ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={14} className="text-green-500" />
              <span className="text-sm text-text-primary">Key Vault configured</span>
            </div>
            <p className="text-xs text-text-muted font-mono">{vaultStatus.vaultUrl}</p>
            <p className="text-xs text-text-muted">
              Source: {vaultStatus.source}
              {vaultStatus.source === "merged" || vaultStatus.source === "company"
                ? " (tenant-specific)"
                : " (platform)"}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-text-muted">
              No dedicated Key Vault for this tenant. Currently using the platform vault.
            </p>
            {hasTenantScopedMcps && (
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-2.5">
                <p className="text-xs text-amber-600">
                  There are tenant-scoped MCP servers in the catalogue. Provision a dedicated Key Vault to enable isolated secret storage for this tenant.
                </p>
              </div>
            )}
            <Button
              size="sm"
              disabled={provisionMutation.isPending}
              onClick={() => provisionMutation.mutate()}
            >
              {provisionMutation.isPending ? (
                <Loader2 size={14} className="mr-1.5 animate-spin" />
              ) : (
                <Building2 size={14} className="mr-1.5" />
              )}
              Provision Tenant Key Vault
            </Button>
            {provisionMutation.isError && (
              <p className="text-xs text-red-500">
                {(provisionMutation.error as Error)?.message || "Failed to provision vault"}
              </p>
            )}
            {provisionMutation.data && (
              <div className="flex items-center gap-2">
                <CheckCircle2 size={14} className="text-green-500" />
                <span className="text-xs text-green-600">
                  {provisionMutation.data.alreadyExists ? "Vault already exists" : "Vault provisioned"}:{" "}
                  {provisionMutation.data.vaultUrl}
                </span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
